<script setup>
import { reactive, onMounted, ref } from 'vue'
import { Link } from '@inertiajs/inertia-vue3'
import { useCheckedBrandsStore } from '@/store/checkedBrands'
import { useCheckedShopsStore } from '@/store/checkedShops'
import { capitalize } from '@/Shared/helper.js'
import List from '@/Shared/List/List'
import useFavorites from '@/Shared/useFavorites.js'
import useCheckbox from '@/Shared/useCheckbox.js'
import ButtonRounded from '@/Shared/Buttons/ButtonRounded'
import Icon from '@/Shared/Icons/Icon'
import ButtonCheckToggle from '@/Shared/Buttons/ButtonCheckToggle'

const props = defineProps({
  category: String,
})

const favorite = reactive({
  favorites: [],
  others: [],
})

const checkbox = reactive({
  checkedList: [],
})

const stores = {
  checkedBrands: useCheckedBrandsStore(),
  checkedShops: useCheckedShopsStore(),
}

const checkedStore = stores[`checked${capitalize(props.category)}s`]

const categoryJ = props.category === 'brand' ? 'ブランド' : 'ストア'
const categoryE = props.category === 'brand' ? 'BRAND' : 'STORE'

const titleRef = ref()
const buttonRef = ref()
const scrollAreaRef = ref()
const containerRef = ref()
const linkURL = ref()
const updateCheckedListRef = ref(Function)

const uncheckAll = () => {
  checkedStore.updateList([])
  setCheckedList()
}

const removeFavorite = () => {
  checkedStore.removeItem('favorite')
}

const setCheckedList = () => {
  checkbox.checkedList = checkedStore.getList
}

const updateCheckedList = (e) => {
  if (e.target.closest('.btn-toggle-star')) return
  updateCheckedListRef.value(e)
}

const updateCheckedListWithArray = (array) => {
  checkedStore.updateList(array)
  setCheckedList()
}

const updateCheckedListWithURL = () => {
  const url = location.href
  const str1 = `/news/all?${props.category}s=`
  const str2 = '&page='
  if (url.includes(str1)) {
    const itemString = url.split(str1)[1].split(str2)[0]
    const itemArray = itemString.split('%20')
    updateCheckedListWithArray(itemArray)
  } else {
    updateCheckedListWithArray([])
  }
}

const updateURL = () => {
  if (typeof window === 'undefined') return
  const list = checkedStore.getList
  const listFiltered = list.filter((item) => item !== 'favorite')
  const str = listFiltered.join('%20')
  const url = listFiltered.length ? `/news/all?${props.category}s=${str}` : '/news/'
  linkURL.value = url
}

const showUncheckBtn = () => {
  const list = checkedStore.getList
  if (!list.length) return false
  if (list.length === 1 && list[0] === 'favorite') return false
  return true
}

const setScrollAreaH = () => {
  const titleH = titleRef.value.clientHeight
  const buttonH = buttonRef.value.clientHeight
  const containerH = containerRef.value.clientHeight
  scrollAreaRef.value.style.maxHeight = `${containerH - titleH - buttonH}px`
}

onMounted(() => {
  const { sortFavoriteList, subscribeFavoriteList } = useFavorites({ favorite: favorite, category: props.category })
  sortFavoriteList()
  subscribeFavoriteList(sortFavoriteList)

  const { updateCheckedList } = useCheckbox({ category: props.category, checkbox: checkbox })
  updateCheckedListRef.value = updateCheckedList
  updateCheckedListWithURL()

  removeFavorite()
  setScrollAreaH()
})
</script>

<template>
  <div ref="containerRef" class="absolute h-full w-full top-0 overflow-hidden px-[8vw] xs:px-[40px]">
    <div class="relative w-full h-full">
      <div ref="titleRef" class="relative pt-[30px] pb-[10px]">
        <div class="font-rift font-bold tracking-[0.01em] text-[60px] xs:text-[64px] sm:text-[68px] md:text-[72px] lg:text-[76px] xl:text-[80px] mb-[0.15em]">{{ categoryE }}</div>
        <div class="font-bold text-[14px] xs:text-[15px]">{{ categoryJ }}で絞り込む</div>
        <ul class="relative flex pt-[20px] [&_li]:relative [&_li]:py-[10px] [&_button]:!w-full [&_.text]:ml-[24px] [&_.text]:mt-[2px]">
          <li>
            <div v-if="favorite.favorites.length" @click="updateCheckedList">
              <ButtonCheckToggle :category="props.category" slug="favorite" />
              <div class="text">お気に入りを選択</div>
            </div>
            <div v-else class="opacity-30">
              <div class="btn-toggle-check">
                <span><Icon type="check" /></span>
              </div>
              <div class="text">お気に入りを選択</div>
            </div>
          </li>
          <li v-if="showUncheckBtn()" class="ml-auto">
            <div class="btn-toggle-check active cursor-pointer w-full" @click="uncheckAll">
              <span><Icon type="check" /></span>
            </div>
            <div class="text">選択を解除</div>
          </li>
        </ul>
      </div>

      <hr class="border-b border-b-black border-dotted" />

      <div ref="scrollAreaRef" class="relative w-full overflow-y-scroll scrollbar-hidden max-h-[calc(100vh_-_300px)]" @click="updateCheckedList">
        <List type="check" :category="props.category" :list="favorite.favorites" :title="`お気に入り${categoryJ}`" />
        <hr class="h-px border-b border-b-black border-dotted" />
        <List type="check" :category="props.category" :list="favorite.others" :title="`${categoryJ}一覧`" />
      </div>

      <div ref="buttonRef" class="absolute bottom-0 w-full pt-[25px] pb-[10vw] xs:pb-[45px] bg-white">
        <Link :href="linkURL">
          <ButtonRounded text="絞り込む" icon="arrow" @click="updateURL" />
        </Link>
      </div>
    </div>
  </div>
</template>
